import { Namespace, TFunction } from 'react-i18next';
import { DeepPartial, FieldNamesMarkedBoolean } from 'react-hook-form';
import { parseISO } from 'date-fns';
import * as Yup from 'yup';
import { AirbnbCancellationPolicy } from 'models/AirbnbCancellationPolicy';
import { AirbnbCheckInOptions } from 'models/AirbnbCheckInOptions';
import { AirbnbGuestRequirements } from 'models/AirbnbGuestRequirements';
import { AreaUnitType } from 'models/AreaUnitType';
import { RoomTypes } from 'models/bookingDotCom/RoomTypes';
import { BOOKING_TYPES, BookingType } from 'models/BookingType';
import { BOOKING_WINDOWS } from 'models/BookingWindow';
import { VrboCancellationPolicy, VrboPromotionPlan } from 'models/Channels';
import { CountryCode } from 'models/Country';
import { CURRENCIES_WITHOUT_NONE, Currency } from 'models/Currency';
import { DAY_OF_WEEK } from 'models/DayOfWeek';
import { PropertyBusinessType } from 'models/Properties';
import { PropertyListingTypes } from 'models/PropertyListingTypes';
import { NewListingDiscountDetermineEnrollmentResponse as VrboDetermineEnrollmentResponse } from 'models/vrbo/NewListingDiscount';
import { decimalFractionToPercentage } from 'utils/numberUtils';
import {
  basePropertyAddressSchema,
  checkInMethod,
  propertyCapacityDetailsSchema,
  propertyDetailsSchema,
} from '../common/Common.schema';
import {
  AirbnbPromotionStatus,
  AirbnbSettings,
  BookingSettings,
  MiscInfo,
  PropertyDetails,
  PropertyMainSettingsData,
  VrboPromotion,
  VrboSettings,
} from './PropertySettingsMainTab.types';
import { CHECK_IN_TIME_END_FLEXIBLE } from './sections/airbnbSettings/AirbnbSettings.constants';
import { VRBO_AVAILABLE_BOOKING_TYPES } from './sections/vrboSettings/PropertyVrboSettings.constants';
import { BOOKING_DOT_COM_AVAILABLE_BOOKING_TYPES } from './sections/bookingSettings/BookingSettings.constants';
import {
  sortVRBOPromotions,
  VRBO_MAXIMUM,
  VRBO_MINIMUM,
} from './sections/vrboSettings/PropertyVrboSettingsPromotionsField';

export const airbnbSettingsGuestRequirementsDefaultValues: Record<
  string,
  AirbnbGuestRequirements
> = {
  [BOOKING_TYPES.BOOKING_REQUEST]: AirbnbGuestRequirements.OFF,
  [BOOKING_TYPES.INSTANT_BOOKING]: AirbnbGuestRequirements.EVERYONE,
};

export const propertySettingsMainTabSchema = (
  businessType: PropertyBusinessType,
  t: TFunction<Namespace<'en'>>,
) => {
  const isHotel = businessType === PropertyBusinessType.HOTEL;
  const isUnitType = businessType === PropertyBusinessType.UNIT_TYPE;

  return Yup.object({
    airbnbSettings: Yup.object({
      airbnbId: Yup.string().nullable().optional(),
      allowBookingRequestAboveMaximumStay: Yup.boolean().optional().nullable(),
      allowBookingRequestWithinBookingLeadTime: Yup.boolean()
        .optional()
        .nullable(),
      bookingType: Yup.string()
        .oneOf([
          null,
          BOOKING_TYPES.BOOKING_REQUEST,
          BOOKING_TYPES.INSTANT_BOOKING,
        ])
        .nullable()
        .when([], {
          is: () => isHotel,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(),
        }),
      cancellationPolicy: Yup.string()
        .oneOf([null, ...Object.values(AirbnbCancellationPolicy)])
        .nullable()
        .when([], {
          is: () => isHotel || isUnitType,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(),
        }),
      checkInOption: Yup.string()
        .oneOf([null, ...Object.values(AirbnbCheckInOptions)])
        .nullable(),
      checkInOptionInstruction: Yup.string().optional().nullable(),
      checkInTimeEnd: Yup.string(),
      checkInTimeStartFlexible: Yup.mixed<boolean | string>()
        .transform((value) => value === 'true')
        .optional()
        .nullable(),
      guestRequirements: Yup.string()
        .oneOf([null, ...Object.values(AirbnbGuestRequirements)])
        .nullable(),
      listingType: Yup.string()
        .oneOf([null, ...Object.values(PropertyListingTypes)])
        .nullable()
        .when([], {
          is: () => isHotel,
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.required(),
        }),
      nonRefundableRateEnabled: Yup.mixed<boolean | string>()
        .transform((value) => value === 'true')
        .optional()
        .nullable(),
      nonRefundableRateDiscount: Yup.number()
        .optional()
        .nullable()
        .when('nonRefundableRateEnabled', {
          is: true,
          then: (schema) =>
            schema
              .useNaNAsNull()
              .useNegativeZeroAsNegative()
              .min(0.01)
              .max(100)
              .required(),
        }),
      sendMessageAsUserId: Yup.string()
        .optional()
        .nullable()
        .transform((value) => (value === '' ? null : value)),
      promotions: Yup.array()
        .of(
          Yup.object({
            type: Yup.string().required(),
            status: Yup.string().required(),
          }),
        )
        .optional()
        .nullable(),
    }),
    bookingDotComSettings: Yup.object({
      alternativeCheckInMethod: checkInMethod(),
      bookingType: Yup.mixed<BookingType>()
        .oneOf(BOOKING_DOT_COM_AVAILABLE_BOOKING_TYPES)
        .optional()
        .nullable(),
      cancellationPolicyCode: Yup.number().nullable(),
      hotelId: Yup.string().nullable(),
      primaryCheckInMethod: checkInMethod(),
      primaryContactUid: Yup.string()
        .transform((value) => (value === '' ? null : value))
        .nullable(),
      roomId: Yup.string().nullable(),
      status: Yup.string().nullable().strip(),
      roomType: Yup.mixed<RoomTypes | null>()
        .oneOf([null, ...Object.values(RoomTypes)])
        .nullable(),
      roomName: Yup.string().nullable(),
    }),
    bookingSettings: Yup.object({
      bookingLeadTime: Yup.number(),
      bookingType: Yup.string().oneOf(Object.values(BOOKING_TYPES)),
      bookingWindow: Yup.number().oneOf(Object.values(BOOKING_WINDOWS)),
      checkInTime: Yup.number(),
      checkoutTime: Yup.number(),
      dayOfWeekMinimumStay: Yup.object({
        [DAY_OF_WEEK.MONDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.TUESDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.WEDNESDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.THURSDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.FRIDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.SATURDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
        [DAY_OF_WEEK.SUNDAY]: Yup.number()
          .optional()
          .nullable()
          .integer()
          .min(0)
          .useNegativeZeroAsNegative()
          .useNaNAsNull(),
      }),
      daysOfTheWeekToCheckInOn: Yup.object({
        [DAY_OF_WEEK.MONDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.TUESDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.WEDNESDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.THURSDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.FRIDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.SATURDAY]: Yup.boolean().optional(),
        [DAY_OF_WEEK.SUNDAY]: Yup.boolean().optional(),
      }),
      fullPaymentTiming: Yup.number()
        .useNaNAsNull()
        .useNegativeZeroAsNegative()
        .integer()
        .min(0)
        .optional()
        .nullable(),
      maximumStay: Yup.number()
        .useNaNAsNull()
        .useNegativeZeroAsNegative()
        .integer()
        .min(0)
        .optional()
        .nullable(),
      minimumStay: Yup.number()
        .useNegativeZeroAsNegative()
        .integer()
        .min(0)
        .when(['maximumStay'], {
          is: (value: number) => value > 0,
          then: (schema) =>
            schema.max(
              Yup.ref('maximumStay'),
              t(
                'pageProperty.mainSettings.bookingSettings.minimumStayMinValidation',
              ),
            ),
        })
        .required(),
      minimumWeekendStay: Yup.number()
        .useNaNAsNull()
        .useNegativeZeroAsNegative()
        .integer()
        .min(0)
        .optional()
        .nullable(),
      percentageUponReservation: Yup.number()
        .useNaNAsNull()
        .useNegativeZeroAsNegative()
        .min(0)
        .max(100)
        .integer()
        .optional()
        .nullable(),
      turnoverDays: Yup.number(),
    }),
    capacityDetails: propertyCapacityDetailsSchema({ businessType, t }),
    propertyAddress: basePropertyAddressSchema(businessType).shape({
      latitude: Yup.number()
        .min(
          -90,
          t('pageProperty.mainSettings.propertyAddress.validation.latitude'),
        )
        .max(
          90,
          t('pageProperty.mainSettings.propertyAddress.validation.latitude'),
        )
        .nullable()
        .transform((current, original) => (original === '' ? null : current)),
      longitude: Yup.number()
        .min(
          -180,
          t('pageProperty.mainSettings.propertyAddress.validation.longitude'),
        )
        .max(
          180,
          t('pageProperty.mainSettings.propertyAddress.validation.longitude'),
        )
        .nullable()
        .transform((current, original) => (original === '' ? null : current)),
    }),
    propertyDetails: propertyDetailsSchema(businessType, t).shape({
      currency: Yup.mixed<Currency>()
        .oneOf(CURRENCIES_WITHOUT_NONE)
        .when([], {
          is: () => isHotel,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      securityDeposit: Yup.number()
        .useNaNAsNull()
        .useNegativeZeroAsNegative()
        .min(0)
        .optional()
        .nullable(),
    }),
    miscInfo: Yup.object({
      accountingId: Yup.string().nullable().optional(),
      externalId: Yup.string().nullable().optional(),
      rentalLicenseNumber: Yup.string().nullable().optional(),
      rentalLicenseExpirationDate: Yup.date().nullable().optional(),
      wifiNetwork: Yup.string().nullable().optional(),
      wifiPassword: Yup.string().nullable().optional(),
    }),
    vrboSettings: Yup.object({
      propertyId: Yup.string().nullable().optional(),
      bookingType: Yup.mixed<BookingType>()
        .oneOf(VRBO_AVAILABLE_BOOKING_TYPES)
        .when([], {
          is: () => isHotel || isUnitType,
          then: (schema) => schema.optional().nullable(),
          otherwise: (schema) => schema.required(),
        }),
      cancellationPolicy: Yup.mixed<VrboCancellationPolicy>().when([], {
        is: () => isHotel || isUnitType,
        then: (schema) => schema.optional().nullable(),
        otherwise: (schema) => schema.required(),
      }),
      customCancellationPolicies: Yup.array()
        .of(
          Yup.object({
            // This is just for render control and should be removed in payloads
            id: Yup.string().strip(),
            refundThreshold: Yup.number().positive().integer().required(),
            refundPercentage: Yup.number()
              .useNaNAsNull()
              .useNegativeZeroAsNegative()
              .positive()
              .integer()
              .min(0)
              .max(100)
              .required(),
          }),
        )
        .when(['cancellationPolicy'], {
          is: VrboCancellationPolicy.CUSTOM,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().nullable(),
        }),
      agentUid: Yup.string()
        .transform((value) => (value === '' ? null : value))
        .nullable(),
      newListingDiscount: Yup.mixed<boolean | string>()
        .transform((value) => value === 'true')
        .optional()
        .nullable(),
      showPropertyExactLocation: Yup.mixed<boolean | string>()
        .transform((value) => value === 'true')
        .when([], {
          is: () => isHotel || isUnitType,
          then: (schema) => schema.optional().nullable(),
          otherwise: (schema) => schema.required(),
        }),
      promotions: Yup.array(
        Yup.object({
          uid: Yup.string().optional(),
          type: Yup.mixed<VrboPromotionPlan>()
            .oneOf(Object.values(VrboPromotionPlan))
            .required(),
          percentage: Yup.number()
            .integer()
            .transform((value, originalValue) =>
              originalValue === '' ? null : value,
            )
            .min(0)
            .max(100)
            .notRequired()
            .nullable()
            .when(
              'type',
              (
                type: VrboPromotionPlan,
                schema: Yup.NumberSchema<number | null | undefined>,
              ) => {
                switch (type) {
                  case VrboPromotionPlan.MOBILE:
                  case VrboPromotionPlan.MEMBER_BLUE:
                  case VrboPromotionPlan.MEMBER_SILVER:
                  case VrboPromotionPlan.MEMBER_GOLD:
                    return schema
                      .min(VRBO_MINIMUM[type])
                      .max(VRBO_MAXIMUM[type]);
                  default:
                    return schema.min(0).max(100);
                }
              },
            ),
        }),
      ).nullable(),
    }),
    propertyNotes: Yup.object({
      notes: Yup.string().nullable().optional(),
      notes_htmlContent: Yup.string().nullable().optional(),
    }),
    // eslint-disable-next-line func-names
  }).test('airbnb-booking-settings-conflict', function (values) {
    const { airbnbSettings, bookingSettings } = values || {};

    // Only validate if Airbnb start time is set to "Default" (false) and we have both settings
    if (
      airbnbSettings?.checkInTimeStartFlexible === false &&
      airbnbSettings?.checkInTimeEnd &&
      airbnbSettings.checkInTimeEnd !== CHECK_IN_TIME_END_FLEXIBLE &&
      bookingSettings?.checkInTime
    ) {
      const airbnbEndTime =
        typeof airbnbSettings.checkInTimeEnd === 'string'
          ? parseInt(airbnbSettings.checkInTimeEnd, 10)
          : airbnbSettings.checkInTimeEnd;

      // If booking settings check-in time is later than airbnb check-in end time, it's invalid
      if (bookingSettings.checkInTime > airbnbEndTime) {
        return this.createError({
          path: 'airbnbSettings.checkInTimeEnd',
          message: t(
            'pageProperty.mainSettings.airbnbSettings.airbnbCheckInTimeConflictError',
          ),
        });
      }
    }

    return true;
  });
};

export type PropertySettingsMainTabFormValues = DeepPartial<
  Yup.InferType<ReturnType<typeof propertySettingsMainTabSchema>>
>;

export type PropertySettingsMainTabFormDirtyFields = Partial<
  Readonly<FieldNamesMarkedBoolean<PropertySettingsMainTabFormValues>>
>;

export type PropertyMainSettingsUpdatePayload = Omit<
  PropertySettingsMainTabFormValues,
  'airbnbSettings' | 'miscInfo' | 'vrboSettings'
> & {
  airbnbSettings?: Partial<AirbnbSettings>;
  bookingSettings?: Partial<BookingSettings>;
  propertyDetails?: Partial<PropertyDetails>;
  vrboSettings?: Partial<VrboSettings>;
  miscInfo?: Partial<MiscInfo>;
};

export const getFormDefaultValues = ({
  mainSettingsData,
  vrboDetermineEnrollment,
}: {
  mainSettingsData?: Partial<PropertyMainSettingsData>;
  vrboDetermineEnrollment?: VrboDetermineEnrollmentResponse;
}): PropertySettingsMainTabFormValues => {
  // If property already exists, then return the data from the server
  if (mainSettingsData) {
    const {
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      airbnbSettings: { checkInTimeEndFlexible, ...airbnbSettings },
      bookingSettings: {
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        bookingLeadTime,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        checkInTime,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        checkoutTime,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        daysOfTheWeekToCheckInOn,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        fullPaymentTiming,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        maximumStay,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        minimumStay,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        minimumWeekendStay,
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        percentageUponReservation,
      },
      bookingDotComSettings,
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      capacityDetails: { rooms },
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      propertyDetails: { propertySizeUnit, propertyType },
      ...restMainSettingsData
    } = mainSettingsData;

    const airbnbSettingsBookingType =
      airbnbSettings?.bookingType ?? BOOKING_TYPES.BOOKING_REQUEST;
    const airbnbSettingsGuestRequirements =
      airbnbSettings?.guestRequirements ??
      airbnbSettingsGuestRequirementsDefaultValues[airbnbSettingsBookingType];
    const airbnbListingType =
      airbnbSettings?.listingType ?? PropertyListingTypes.ENTIREHOME;

    const promotions = [...(mainSettingsData?.vrboSettings?.promotions ?? [])];

    if (!promotions.find((promo) => promo.type === VrboPromotionPlan.MOBILE)) {
      promotions.push({
        type: VrboPromotionPlan.MOBILE,
        percentage: null,
      } as VrboPromotion);
    }

    if (
      !promotions.find((promo) => promo.type === VrboPromotionPlan.MEMBER_BLUE)
    ) {
      promotions.push({
        type: VrboPromotionPlan.MEMBER_BLUE,
        percentage: null,
      } as VrboPromotion);
    }

    if (
      !promotions.find(
        (promo) => promo.type === VrboPromotionPlan.MEMBER_SILVER,
      )
    ) {
      promotions.push({
        type: VrboPromotionPlan.MEMBER_SILVER,
        percentage: null,
      } as VrboPromotion);
    }

    if (
      !promotions.find((promo) => promo.type === VrboPromotionPlan.MEMBER_GOLD)
    ) {
      promotions.push({
        type: VrboPromotionPlan.MEMBER_GOLD,
        percentage: null,
      } as VrboPromotion);
    }

    return {
      ...restMainSettingsData,
      airbnbSettings: {
        ...airbnbSettings,
        promotions: airbnbSettings?.promotions?.filter(
          (promotion) => promotion.status !== AirbnbPromotionStatus.EXPIRED,
        ),
        bookingType: airbnbSettingsBookingType,
        checkInTimeEnd: checkInTimeEndFlexible
          ? CHECK_IN_TIME_END_FLEXIBLE
          : `${airbnbSettings.checkInTimeEnd}`,
        checkInTimeStartFlexible: `${airbnbSettings.checkInTimeStartFlexible}`,
        guestRequirements: airbnbSettingsGuestRequirements,
        listingType: airbnbListingType,
        nonRefundableRateEnabled: `${airbnbSettings.nonRefundableRateEnabled}`,
        nonRefundableRateDiscount:
          typeof airbnbSettings.nonRefundableRateDiscount === 'number'
            ? decimalFractionToPercentage(
                airbnbSettings.nonRefundableRateDiscount,
              )
            : airbnbSettings.nonRefundableRateDiscount,
      },
      bookingDotComSettings: {
        ...bookingDotComSettings,
        alternativeCheckInMethod: {
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          type: bookingDotComSettings.alternativeCheckInMethod?.checkinMethod,
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          ...bookingDotComSettings.alternativeCheckInMethod,
        },
        bookingType:
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          bookingDotComSettings.bookingType ?? BOOKING_TYPES.INSTANT_BOOKING,
        primaryCheckInMethod: {
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          type: bookingDotComSettings.primaryCheckInMethod?.checkinMethod,
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          ...bookingDotComSettings.primaryCheckInMethod,
        },
        primaryContactUid:
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          bookingDotComSettings.primaryContactUid === null
            ? ''
            : // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
              bookingDotComSettings.primaryContactUid,
      },
      bookingSettings: {
        ...mainSettingsData.bookingSettings,
        bookingLeadTime: bookingLeadTime ?? 48,
        checkInTime: checkInTime ?? 15,
        checkoutTime: checkoutTime ?? 11,
        daysOfTheWeekToCheckInOn: {
          [DAY_OF_WEEK.MONDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.MONDAY,
          ),
          [DAY_OF_WEEK.TUESDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.TUESDAY,
          ),
          [DAY_OF_WEEK.WEDNESDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.WEDNESDAY,
          ),
          [DAY_OF_WEEK.THURSDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.THURSDAY,
          ),
          [DAY_OF_WEEK.FRIDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.FRIDAY,
          ),
          [DAY_OF_WEEK.SATURDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.SATURDAY,
          ),
          [DAY_OF_WEEK.SUNDAY]: daysOfTheWeekToCheckInOn?.includes(
            DAY_OF_WEEK.SUNDAY,
          ),
        },
        fullPaymentTiming: fullPaymentTiming ?? 45,
        maximumStay: maximumStay ?? 0,
        minimumStay: minimumStay ?? 1,
        minimumWeekendStay: minimumWeekendStay ?? 0,
        percentageUponReservation:
          typeof percentageUponReservation === 'number'
            ? decimalFractionToPercentage(percentageUponReservation)
            : 0,
      },
      capacityDetails: {
        ...mainSettingsData.capacityDetails,
        // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        rooms: rooms?.map(({ beds, ...rest }) => ({
          ...rest,
          beds: Object.entries(beds).map(([bedType, count]) => ({
            bedType,
            count,
          })),
        })),
      },
      miscInfo: {
        ...mainSettingsData.miscInfo,
        // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        rentalLicenseExpirationDate:
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          mainSettingsData.miscInfo.rentalLicenseExpirationDate &&
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          parseISO(mainSettingsData.miscInfo.rentalLicenseExpirationDate),
      },
      propertyAddress: {
        ...mainSettingsData.propertyAddress,
        countryCode:
          mainSettingsData?.propertyAddress?.countryCode ?? CountryCode.US,
        state: mainSettingsData?.propertyAddress?.state ?? 'AL',
      },
      propertyDetails: {
        ...mainSettingsData.propertyDetails,
        propertySizeUnit: propertySizeUnit || AreaUnitType.SQUARE_METERS,
        propertyType: propertyType ?? 'HOUSE',
      },
      vrboSettings: {
        ...mainSettingsData.vrboSettings,
        agentUid:
          mainSettingsData?.vrboSettings?.agentUid === null
            ? ''
            : mainSettingsData?.vrboSettings?.agentUid,
        customCancellationPolicies:
          mainSettingsData?.vrboSettings?.customCancellationPolicies?.map(
            (customPolicy) => ({
              id: crypto.randomUUID(),
              ...customPolicy,
            }),
          ) ?? [],
        newListingDiscount: vrboDetermineEnrollment?.enrolled
          ? 'true'
          : 'false',
        showPropertyExactLocation: `${mainSettingsData?.vrboSettings?.showPropertyExactLocation}`,
        promotions: promotions.sort(sortVRBOPromotions),
      },
      propertyNotes: {
        notes: mainSettingsData?.propertyNotes?.notes ?? '',
        notes_htmlContent: mainSettingsData?.propertyNotes?.notes ?? '',
      },
    };
  }

  return {
    airbnbSettings: {
      bookingType: BOOKING_TYPES.BOOKING_REQUEST,
      checkInTimeEnd: CHECK_IN_TIME_END_FLEXIBLE,
      guestRequirements:
        airbnbSettingsGuestRequirementsDefaultValues[
          BOOKING_TYPES.BOOKING_REQUEST
        ],
    },
    bookingDotComSettings: {},
    bookingSettings: {
      bookingLeadTime: 48,
      checkInTime: 15,
      checkoutTime: 11,
      daysOfTheWeekToCheckInOn: {
        [DAY_OF_WEEK.MONDAY]: false,
        [DAY_OF_WEEK.TUESDAY]: false,
        [DAY_OF_WEEK.WEDNESDAY]: false,
        [DAY_OF_WEEK.THURSDAY]: false,
        [DAY_OF_WEEK.FRIDAY]: false,
        [DAY_OF_WEEK.SATURDAY]: false,
        [DAY_OF_WEEK.SUNDAY]: false,
      },
      fullPaymentTiming: 45,
      maximumStay: 0,
      minimumStay: 1,
      minimumWeekendStay: 0,
      percentageUponReservation: 50,
    },
    propertyAddress: {
      countryCode: CountryCode.US,
      state: 'AL',
    },
    propertyDetails: {
      propertySizeUnit: AreaUnitType.SQUARE_METERS,
      propertyType: 'HOUSE',
    },
    vrboSettings: {
      bookingType: BOOKING_TYPES.INSTANT_BOOKING,
      cancellationPolicy: VrboCancellationPolicy.NO_REFUND,
      newListingDiscount: 'false',
      showPropertyExactLocation: 'true',
    },
  };
};
